/* eslint-disable @next/next/no-img-element -- logo sources are swapped imperatively while hidden */
"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Monogram } from "@/components/ui/Logo";

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/* ================================================================== */
/*  CONFIG — the only things you normally need to edit                 */
/* ================================================================== */

export type OrbitLogo = { src: string; alt: string };

/** Secondary logos — Quikit app icons in /public/Quikit all logos. Add, remove or reorder freely. */
export const logos: OrbitLogo[] = [
  { src: "/Quikit all logos/Frame 2095586846.svg", alt: "QuikCRM" },
  { src: "/Quikit all logos/Frame 2095586784.svg", alt: "QuikPeople" },
  { src: "/Quikit all logos/Frame 2095586794.svg", alt: "QuikProjects" },
  { src: "/Quikit all logos/Frame 2095586810.svg", alt: "QuikFinance" },
  { src: "/Quikit all logos/Frame 2095586866.svg", alt: "QuikAttendance" },
  { src: "/Quikit all logos/Frame 2095586806.svg", alt: "QuikChat" },
  { src: "/Quikit all logos/Frame 2095586864.svg", alt: "QuikPurchase" },
  { src: "/Quikit all logos/Frame 2095586819.svg", alt: "QuikSkill" },
  { src: "/Quikit all logos/Frame 2095586852.svg", alt: "QuikAsset" },
  { src: "/Quikit all logos/Frame 2095586759.svg", alt: "QuikScale" },
  { src: "/Quikit all logos/Frame 2095587766.svg", alt: "QuikInsights" },
  { src: "/Quikit all logos/Frame 2095586845.svg", alt: "QuikVision" },
  { src: "/Quikit all logos/Frame 2095586760.svg", alt: "QuikSocial" },
  { src: "/Quikit all logos/Frame 2095586817.svg", alt: "QuikSEO" },
  { src: "/Quikit all logos/Frame 2095586808.svg", alt: "QuikEmail" },
  { src: "/Quikit all logos/Frame 2095586863.svg", alt: "QuikSales" },
  { src: "/Quikit all logos/Frame 2095586796.svg", alt: "QuikSupport" },
  { src: "/Quikit all logos/Frame 2095586821.svg", alt: "QuikDocs" },
  { src: "/Quikit all logos/Frame 2095587769.svg", alt: "QuikSheets" },
  { src: "/Quikit all logos/Frame 2095587770.svg", alt: "QuikSlides" },
  { src: "/Quikit all logos/Frame 2095586861.svg", alt: "QuikForms" },
  { src: "/Quikit all logos/Frame 2095586862.svg", alt: "QuikSigns" },
  { src: "/Quikit all logos/Frame 2095586812.svg", alt: "QuikInfra" },
  { src: "/Quikit all logos/Frame 2095586855-1.svg", alt: "QuikStudio" },
  { src: "/Quikit all logos/Frame 2095586855.svg", alt: "Developer workflows" },
  { src: "/Quikit all logos/Frame 2095586820.svg", alt: "Code docs" },
  { src: "/Quikit all logos/Frame 2095586760-1.svg", alt: "Channels" },
];

/** Per-breakpoint layout. `perSide` = logos visible on each side of the monogram. */
const LAYOUTS = [
  { minWidth: 1024, perSide: 5, logoSize: 46, centerSize: 96, maxHalfWidth: 520 },
  { minWidth: 640, perSide: 4, logoSize: 40, centerSize: 84, maxHalfWidth: 400 },
  { minWidth: 0, perSide: 3, logoSize: 34, centerSize: 72, maxHalfWidth: 190 },
] as const;

type Props = {
  logos?: OrbitLogo[];
  /** Optional image for the central mark. Default: the built-in, pixel-exact Quikit monogram. */
  centerSrc?: string;
  /** Seconds for a logo to advance one slot. Higher = slower. Reference ≈ 3.5. */
  secondsPerSlot?: number;
  /** How far the arc drops at its ends, as a fraction of its half-width. 0 = straight line. */
  curvature?: number;
  /** Multiplier on the gap between logos (1 = spread evenly across the arc). */
  spacing?: number;
  /** Multiplier on logo sizes (satellites and monogram). */
  size?: number;
  className?: string;
};

/* ================================================================== */
/*  Motion model                                                       */
/* ================================================================== */
/*
 * Two mirrored streams run along one dome-shaped arc. Each satellite has a progress
 * u ∈ [0, 1): 0 = the outer end of its side, 1 = the centre. All satellites share the
 * same speed, so spacing stays constant. They fade in over the outer part of the path,
 * grow slightly as they approach, and slide *under* the monogram — where they wrap back
 * to the edge (unseen) and pick up the next logo from the list.
 */
const FADE_IN = 0.18; // portion of the path used to fade in
const SCALE_EDGE = 0.8;
const SCALE_CENTER = 1.05;

const smooth = (x: number) => x * x * (3 - 2 * x);

type Geometry = { half: number; drop: number; radius: number; top: number };

function arcY(x: number, g: Geometry) {
  // Circle through (±half, drop) and (0, 0): flat at the top, falling off towards the ends.
  if (g.drop <= 0) return 0;
  return g.radius - Math.sqrt(Math.max(0, g.radius * g.radius - x * x));
}

export function QuikitLogoAnimation({
  logos: items = logos,
  centerSrc,
  secondsPerSlot = 3.5,
  curvature = 0.26,
  spacing = 1,
  size = 1,
  className,
}: Props) {
  const root = useRef<HTMLDivElement>(null);
  const chips = useRef<(HTMLDivElement | null)[]>([]);
  const imgs = useRef<(HTMLImageElement | null)[]>([]);
  const track = useRef<SVGPathElement>(null);
  const geo = useRef<Geometry>({ half: 400, drop: 100, radius: 850, top: 60 });
  /** Icons confirmed decoded, and the Image objects holding them open. */
  const ready = useRef<Set<string>>(new Set());
  const warmed = useRef<HTMLImageElement[]>([]);
  const [layout, setLayout] = useState<(typeof LAYOUTS)[number]>(LAYOUTS[0]);

  const perSide = layout.perSide;
  const slots = perSide + 1; // one extra per side is always fading in at the edge
  const logoSize = Math.round(layout.logoSize * size);
  const centerSize = Math.round(layout.centerSize * size);
  const height = Math.round(centerSize * 0.5 + logoSize * 0.75 + curvature * layout.maxHalfWidth + 40);

  /* Measure the stage: pick the layout and derive the arc geometry. A layout effect, so the
   * stage has its real height before the browser paints and before anything measures the page
   * (a restored scroll position on Back would otherwise land short by the height change). */
  useIsoLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const next = LAYOUTS.find((l) => window.innerWidth >= l.minWidth) ?? LAYOUTS[LAYOUTS.length - 1];
      setLayout((cur) => (cur === next ? cur : next));
      const half = Math.max(80, Math.min(next.maxHalfWidth, w / 2 - next.logoSize * size * 0.6));
      const drop = half * curvature;
      const radius = drop > 0 ? (half * half + drop * drop) / (2 * drop) : Infinity;
      geo.current = { half, drop, radius, top: next.centerSize * size * 0.5 + 12 };
      if (track.current) {
        const pts: string[] = [];
        for (let i = 0; i <= 40; i++) {
          const x = -half + (i / 40) * half * 2;
          pts.push(`${i ? "L" : "M"}${(w / 2 + x).toFixed(1)} ${(geo.current.top + arcY(x, geo.current)).toFixed(1)}`);
        }
        track.current.setAttribute("d", pts.join(" "));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [curvature, size]);

  /* Fetch every icon up front, and record which ones have actually arrived.
   *
   * The objects are kept in a ref rather than dropped on the floor: an Image with no live
   * reference can be collected while its request is still in flight, and the browser is then
   * free to abandon the fetch — one of the ways a tile used to appear empty. These files are
   * also served with `max-age=0`, so a swap to one that was never really fetched means a
   * round trip with nothing to paint until it returns. */
  useEffect(() => {
    warmed.current = items.map((logo) => {
      const pre = new Image();
      const done = () => ready.current.add(logo.src);
      pre.onload = done;
      pre.src = logo.src;
      // A file already in the cache can finish before the handler is attached.
      if (pre.complete && pre.naturalWidth > 0) done();
      return pre;
    });
    return () => {
      warmed.current = [];
    };
  }, [items]);

  /* The loop — writes transforms straight to the DOM, no React state per frame. */
  useEffect(() => {
    const el = root.current;
    if (!el || !items.length) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const total = slots * 2;
    const prevU = new Array<number>(total).fill(0);
    let nextLogo = total % items.length;
    let t = 0;
    let last = performance.now();
    let raf = 0;
    let visible = true;

    const place = () => {
      const g = geo.current;
      const cx = el.clientWidth / 2;
      for (let i = 0; i < total; i++) {
        const chip = chips.current[i];
        if (!chip) continue;
        const side = i < slots ? -1 : 1;
        const k = i % slots;
        // As in the reference, the right stream runs half a slot behind the left one,
        // so logos reach the centre alternately rather than in lockstep.
        const phase = side === 1 ? 0.5 : 0;
        const u = (((t / secondsPerSlot + k + phase) / slots) % 1 + 1) % 1;

        // Wrapped from the centre back to the edge: hand it the next logo while hidden.
        if (u < prevU[i] && !reduce) {
          const img = imgs.current[i];
          const logo = items[nextLogo];
          // Only swap in an icon that has already loaded. A chip begins fading in the instant
          // it wraps, so handing it a file that is still in flight paints an empty tile (the
          // browser falls back to the alt text) until the bytes land. Keeping the icon it
          // already has for one more pass is the better failure mode — and the cursor does
          // not advance, so the next wrap tries the same icon again and nothing is skipped.
          if (img && logo && ready.current.has(logo.src)) {
            img.src = logo.src;
            img.alt = logo.alt;
            nextLogo = (nextLogo + 1) % items.length;
          }
        }
        prevU[i] = u;

        const x = side * g.half * spacing * (1 - u);
        const y = g.top + arcY(x, g);
        const scale = SCALE_EDGE + (SCALE_CENTER - SCALE_EDGE) * u;
        const opacity = smooth(Math.min(1, u / FADE_IN));
        chip.style.transform = `translate3d(${(cx + x).toFixed(2)}px, ${y.toFixed(2)}px, 0) translate(-50%, -50%) scale(${scale.toFixed(3)})`;
        chip.style.opacity = opacity.toFixed(3);
      }
    };

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      t += dt;
      place();
      if (visible) raf = requestAnimationFrame(tick);
    };

    place();
    if (reduce) return;

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      cancelAnimationFrame(raf);
      if (visible) {
        last = performance.now();
        raf = requestAnimationFrame(tick);
      }
    });
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [items, slots, secondsPerSlot, spacing]);

  return (
    <div
      ref={root}
      role="img"
      aria-label="Quikit connects with the tools your team already uses"
      className={cn("relative w-full select-none", className)}
      style={{ height }}
    >
      {/* Faint track the logos ride along */}
      <svg aria-hidden="true" className="pointer-events-none absolute inset-0 size-full">
        <defs>
          <linearGradient id="qla-track" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset="0.5" stopColor="#fff" stopOpacity="0.14" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path ref={track} fill="none" stroke="url(#qla-track)" strokeWidth="1" />
      </svg>

      {/* Satellites — z-index 1 */}
      {Array.from({ length: slots * 2 }).map((_, i) => {
        const logo = items[i % items.length];
        return (
          <div
            key={`${perSide}-${i}`}
            ref={(node) => {
              chips.current[i] = node;
            }}
            aria-hidden="true"
            className="absolute left-0 top-0 z-[1] overflow-hidden rounded-[16.4%] opacity-0 shadow-[0_8px_20px_-8px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.06)] will-change-transform"
            style={{ width: logoSize, height: logoSize }}
          >
            <img
              ref={(node) => {
                imgs.current[i] = node;
              }}
              src={logo?.src}
              alt={logo?.alt ?? ""}
              draggable={false}
              decoding="async"
              className="block size-full"
            />
          </div>
        );
      })}

      {/* Central Quikit monogram — z-index 10, never moves */}
      <div
        className="absolute left-1/2 z-10 -translate-x-1/2 overflow-hidden rounded-[16.4%] bg-[#111111] shadow-[0_0_0_1px_rgba(255,255,255,0.16),0_24px_60px_-18px_rgba(0,0,0,0.9),0_0_48px_-8px_rgba(45,226,208,0.35)]"
        style={{ top: 12, width: centerSize, height: centerSize }}
      >
        {centerSrc ? (
          <img src={centerSrc} alt="Quikit" draggable={false} className="size-full object-cover" />
        ) : (
          <Monogram className="size-full" />
        )}
      </div>
    </div>
  );
}
